import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { copyTextToClipboard, splitForumsHost, splitWebAddresses } from '../../core/error-text';
import { ErrorStateService, HelpTip } from '../../core/error-state.service';
import { DecodeEntitiesPipe } from '../pipes/decode-entities.pipe';
import { LinkedTextComponent } from './linked-text.component';

export type TipKind = 'article' | 'answered' | 'question';

//
// A tip as the window shows it: the kind says which icon and which text for a screen reader, and a tip in
// another language than the page names that language.
//
export interface TipView
{
  title: string;
  url: string;
  kind: TipKind;
  kindKey: string;
  otherLanguage: string | null;
}

const TIP_KIND_KEYS: Record<TipKind, string> =
{
  article: 'itgen_wiki_article'
, answered: 'itgen_answered_question'
, question: 'itgen_open_question'
};

//
// Two close affordances with different behaviour: the cross at the top right reloads the page, as the
// requests of this page are switched off after a fatal error and only a new page can retry; the OK button
// just closes the message. Escape closes it as OK does.
//
@Component({
  selector: 'app-error-modal',
  standalone: true,
  imports: [TranslateDirective, TranslatePipe, DecodeEntitiesPipe, LinkedTextComponent],
  styles: [':host { display: block; }'],
  templateUrl: './error-modal.component.html',
})
export class ErrorModalComponent
{
  protected readonly errorState = inject(ErrorStateService);
  private readonly translate = inject(TranslateService);

  protected readonly splitWebAddresses = splitWebAddresses;
  protected readonly splitForumsHost = splitForumsHost;

  protected readonly detailsOpen = signal(false);
  protected readonly copiedTarget = signal<'message' | 'requestId' | null>(null);

  //
  // The language of the page: the one the language selector shows. Before the profile is read it is the
  // fallback language.
  //
  private readonly language = toSignal
  (
    this.translate.onLangChange.pipe(map(event => event.lang))
  , { initialValue: this.translate.getCurrentLang() ?? this.translate.getFallbackLang() ?? 'en' }
  );

  //
  // The tips in the language of the page come first, each group in the order of the service.
  //
  protected readonly tips = computed<TipView[]>(() =>
  {
    const language = (this.language() ?? '').substring(0, 2).toLowerCase();
    const isOther = (tip: HelpTip) => tip.languageCode !== '' && tip.languageCode !== language;

    return this.errorState.errorModal().helpTips
      .map((tip, position) => ({ tip, position }))
      .sort((left, right) => Number(isOther(left.tip)) - Number(isOther(right.tip)) || left.position - right.position)
      .map(({ tip }) =>
      {
        const kind: TipKind = !tip.isQuestion ? 'article' : tip.hasAnswer ? 'answered' : 'question';

        return { title: tip.title, url: tip.readMoreUrl, kind, kindKey: TIP_KIND_KEYS[kind], otherLanguage: isOther(tip) ? tip.languageCode : null };
      });
  });

  constructor()
  {
    //
    // A message starts with its details closed, and OK takes the focus when the message opens: this release has
    // no dialog directive. The wait is for the display binding, which has not been applied yet when the state changes.
    //
    const visible = computed(() => this.errorState.errorModal().visible);

    effect(() =>
    {
      if (visible())
      {
        setTimeout(() => document.getElementById('btn-error-ok')?.focus(), 60);
      }
      else
      {
        this.detailsOpen.set(false);
      }
    });
  }

  reloadPage(): void
  {
    window.location.reload();
  }

  toggleDetails(): void
  {
    this.detailsOpen.update(open => !open);

    if (this.detailsOpen())
    {
      setTimeout(() => document.getElementById('error-details')?.scrollIntoView({ block: 'nearest' }), 0);
    }
  }

  copyRequestId(): void
  {
    this.copy('requestId', this.errorState.errorModal().requestId);
  }

  //
  // The message as one text to paste into a mail or a forum post: the product, then what the message shows.
  //
  copyMessage(): void
  {
    const modal = this.errorState.errorModal();
    const lines = ['Get My Report', '', `${modal.messageCode}: ${modal.message}`];

    const add = (label: string, value: string | null | undefined) =>
    {
      if (value)
      {
        lines.push(label ? `${label}: ${value}` : value);
      }
    };

    add('', modal.showKindRequest ? modal.kindRequest : '');
    add(this.translate.instant('itgen_gmr_natural_key'), modal.showNaturalKeyWrapper ? modal.naturalKey : '');
    lines.push('');
    add('Request ID', modal.requestId);
    add(this.translate.instant('itgen_message_id'), modal.uid);
    add(this.translate.instant('itgen_occurred_utc'), modal.timeOccurredUtc);
    add(this.translate.instant('itgen_request'), modal.showRequestWrapper ? `${modal.requestStatus ?? ''} ${modal.requestUrl ?? ''}`.trim() : '');

    this.copy('message', lines.join('\n'));
  }

  //
  // Copy a text and tell the user so, on the button and for a screen reader; the label returns after two
  // seconds. The browser can refuse the clipboard; the text then stays on the page for the user to select.
  //
  private copy(target: 'message' | 'requestId', text: string): void
  {
    if (this.copiedTarget() !== null)
    {
      return;
    }

    copyTextToClipboard(text).then
    (
      () =>
      {
        this.copiedTarget.set(target);
        setTimeout(() => this.copiedTarget.set(null), 2000);
      }
    , () => { }
    );
  }
}
